import React, { useState, useEffect, useCallback, useContext } from "react";
import type { FC } from "react";
import {
  Row,
  Col,
  Button,
  Divider,
  Modal,
  Select,
  Input,
  Spin,
  Tooltip,
  Form,
  Drawer,
  message,
} from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { logOut, ToFirstContainerRegistry } from "../../../security";
import DistributionPointSelected from "../../../context/MyDistributionPoint";
import { useContainerValidation } from "../useContainerValidation";
import TankFields from "./TankFields";
import {
  CatalogInstallationKeys,
  CatalogTankKeys,
  createContainer,
  getCompanyInstallationKey,
  getTankKeys,
  getWareHousesByDistributionPoint,
  ResponseGetAllWareHoses,
} from "api/generated";
import { useClient } from "hooks/useClient";
import { handleAxiosError } from "common/helpers/generalUtils";

const { Option } = Select;

interface ContainerAggregationProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  isInitialConfiguration?: boolean;
}

export const ContainerAggregation: FC<ContainerAggregationProps> = ({
  open,
  onClose,
  onSuccess,
  isInitialConfiguration = false,
}) => {
  const [form] = Form.useForm();

  const { distributionPoint } = useContext(DistributionPointSelected);
  const distributionPointId = distributionPoint?.id;

  const { client } = useClient();

  const [warehouses, setWarehouses] = useState<ResponseGetAllWareHoses[]>([]);
  const [tankKeys, setTankKeys] = useState<CatalogTankKeys[]>([]);
  const [installationKey, setInstallationKey] =
    useState<CatalogInstallationKeys>();
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const validation = useContainerValidation();
  const isWarehouseSelected = Form.useWatch("idWareHouse", form) !== undefined;

  const fetchWarehouses = useCallback(() => {
    getWareHousesByDistributionPoint({
      client,
      path: { distributionPointId: distributionPointId ?? "" },
    })
      .then(({ data }) => setWarehouses(data ?? []))
      .catch((err) =>
        handleAxiosError(err, "No se pudieron obtener almacenes"),
      );
  }, [distributionPointId, client]);

  useEffect(() => {
    fetchWarehouses();

    getCompanyInstallationKey({ client })
      .then(({ data }) => setInstallationKey(data))
      .catch((err) =>
        handleAxiosError(
          err,
          "No se pudieron obtener las claves de instalación",
        ),
      );

    getTankKeys({ client })
      .then(({ data }) => setTankKeys(data ?? []))
      .catch((err) =>
        handleAxiosError(err, "No se pudieron obtener las claves de tanques"),
      );
  }, [fetchWarehouses, client]);

  useEffect(() => {
    const handler = () => {
      form.setFieldValue("idWareHouse", undefined);
      fetchWarehouses();
    };
    window.addEventListener("distributionPointChange", handler);
    return () => window.removeEventListener("distributionPointChange", handler);
  }, [fetchWarehouses, form]);

  const preSetFieldsFromWarehouse = useCallback(
    (warehouseId: string) => {
      const warehouse = warehouses.find((w) => w.idWareHouse === warehouseId);
      if (!warehouse || !warehouse.warehouseKey) return;
      const parts = warehouse.warehouseKey.split("-");
      form.setFieldsValue({ tankType: parts[0], instalationKey: parts[1] });
    },
    [warehouses, form],
  );

  useEffect(() => {
    if (warehouses.length === 1) {
      const singleWarehouse = warehouses[0];
      form.setFieldValue("idWareHouse", singleWarehouse.idWareHouse);
      preSetFieldsFromWarehouse(singleWarehouse.idWareHouse ?? "");
    }
  }, [warehouses, form, preSetFieldsFromWarehouse]);

  const obtainTankCode = useCallback(() => {
    const tankType = form.getFieldValue("tankType");
    const instalationKey = form.getFieldValue("instalationKey");
    const customerTankKey = form.getFieldValue("customerTankKey");

    if (tankType && instalationKey && customerTankKey) {
      const key = `${tankType}-${instalationKey}-${customerTankKey}`;
      form.setFieldValue("containerKey", key);
      form.setFieldValue("meterIdentificationKey", `SME-${key}`);
      validation.checkContainerKey(key, form.getFieldValue("idWareHouse"));
    } else {
      form.setFieldValue("containerKey", "");
      form.setFieldValue("meterIdentificationKey", "");
    }
  }, [form, validation]);

  const handleCustomerTankKeyBlur = useCallback(
    (value: string) => {
      if (validation.validateTankDuctKey(value)) {
        obtainTankCode();
      } else {
        form.setFieldValue("containerKey", "");
      }
    },
    [validation, obtainTankCode, form],
  );

  const handleFormFinish = useCallback(() => {
    setConfirmVisible(true);
  }, []);

  const handleConfirmOk = useCallback(() => {
    setLoading(true);
    const values = form.getFieldsValue(true);

    createContainer({
      client,
      body: {
        idContainer: "",
        containerKey: values.containerKey,
        containerType: "TANQUE",
        containerOperationalCapacity: values.containerOperationalCapacity,
        containerUsefulCapacity: values.containerUsefulCapacity,
        containerTotalCapacity: values.containerTotalCapacity,
        containerHeelGasCapacity: values.containerHeelGasCapacity,
        containerFundingCapacity: values.containerFundingCapacity,
        containerStatus: true,
        containerMinOperationalCapacity: values.containerMinOperationalCapacity,
        containerDescription: values.containerDescription,
        containerCalibrationDate: values.containerCalibrationDate?.format(
          "YYYY-MM-DD HH:mm:ss",
        ),
        containerInitialVolume: values.containerInitialVolume,
        integrationCode: values.integrationCode,
        idWareHouse: values.idWareHouse,
        meterIdentificationKey: values.meterIdentificationKey,
        measurementUncertainty: values.measurementUncertainty,
      },
    })
      .then(({ data }) => message.success(data?.createdMessage))
      .catch((err) =>
        handleAxiosError(err, "No se ha podido crear el contenedor"),
      )
      .finally(() => {
        finalAction();
        setLoading(false);
        setConfirmVisible(false);
      });
  }, [form, client]);

  const finalAction = useCallback(() => {
    if (isInitialConfiguration) {
      Modal.confirm({
        title: "¿Quieres agregar más contenedores?",
        content:
          'En caso de requerir seguir agregando más información, da click en "Continuar"; en caso contrario, da click en "Finalizar configuración inicial".',
        okText: "Finalizar configuración inicial",
        cancelText: "Continuar",
        onCancel: () => ToFirstContainerRegistry(),
        onOk: () => logOut(),
      });
    } else {
      onSuccess?.();
    }
  }, [isInitialConfiguration, onSuccess]);

  const handleIntegrationCodeBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value) validation.checkIntegrationCode(value);
    },
    [validation],
  );

  return (
    <Drawer
      title="Agregar contenedor"
      placement="right"
      width={1200}
      open={open}
      onClose={onClose}
      maskClosable={false}
      styles={{
        mask: {
          backdropFilter: "blur(10px)",
        },
      }}
      footer={
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button icon={<CloseOutlined />} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            disabled={validation.integrationCodeVS?.validateStatus}
            loading={loading}
            onClick={() => form.submit()}
            className="containers-btn--add"
          >
            Agregar contenedor
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFormFinish}
        initialValues={{ containerType: "Tanque" }}
        scrollToFirstError
      >
        <Divider orientation="left">Contenedor</Divider>
        <p style={{ color: "#64748b", marginBottom: 24 }}>
          Llena los campos a continuación para agregar un contenedor
        </p>

        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Tooltip title="Tipo de contenedor">
              <Form.Item name="containerType" label="Tipo de contenedor">
                <Input disabled />
              </Form.Item>
            </Tooltip>
          </Col>
          <Col xs={24} md={12}>
            <Tooltip title="Almacén al que pertenece el contenedor">
              <Form.Item
                name="idWareHouse"
                label="Almacén"
                rules={[{ required: true, message: "Selecciona el almacén." }]}
              >
                <Select
                  onChange={(warehouseId: string) => {
                    form.setFieldValue("customerTankKey", undefined);
                    preSetFieldsFromWarehouse(warehouseId);
                  }}
                  placeholder="Selecciona un almacén"
                >
                  {warehouses.map((w) => (
                    <Option key={w.idWareHouse} value={w.idWareHouse}>
                      {w.warehouseKey} - {w.products?.productDescription ?? ""}{" "}
                      - {w.subproducts?.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Tooltip>
          </Col>
          <Col xs={12} md={6}>
            <Tooltip title="Código de integración del contenedor">
              <Form.Item
                name="integrationCode"
                label="Código de Integración"
                validateStatus={
                  validation.integrationCodeVS?.validateStatus
                    ? "error"
                    : "success"
                }
                help={validation.integrationCodeVS?.errorMsg}
              >
                <Input
                  disabled={!isWarehouseSelected}
                  onBlur={handleIntegrationCodeBlur}
                  placeholder="Opcional"
                />
              </Form.Item>
            </Tooltip>
          </Col>
        </Row>

        <Spin
          spinning={!isWarehouseSelected}
          tip="Selecciona un almacén para continuar"
          size="large"
        >
          <TankFields
            containerCodeVS={validation.containerCodeVS}
            tankAndDuctIdentificationKeyVS={
              validation.tankAndDuctIdentificationKeyVS
            }
            tankKeys={tankKeys}
            installationKey={installationKey}
            onTankKeyBlur={obtainTankCode}
            onCustomerTankKeyBlur={handleCustomerTankKeyBlur}
          />
        </Spin>
      </Form>

      <Modal
        open={confirmVisible}
        title="Atención"
        onOk={handleConfirmOk}
        onCancel={() => setConfirmVisible(false)}
        footer={
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <Button
              key="back"
              onClick={() => setConfirmVisible(false)}
              style={{ flex: "1 1 auto" }}
            >
              Revisar información
            </Button>
            <Button
              key="submit"
              type="primary"
              danger
              loading={loading}
              onClick={handleConfirmOk}
              style={{ flex: "1 1 auto" }}
            >
              Agregar contenedor
            </Button>
          </div>
        }
      >
        <p>
          Revisa a detalle que todos los campos ingresados sean correctos, pues
          una vez que presiones &quot;Agregar contenedor&quot; la edición de
          estos se verá limitada.
        </p>
      </Modal>
    </Drawer>
  );
};

export default ContainerAggregation;
